import React, { createContext, useEffect, useState, useContext, useCallback, useMemo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { VipCardContext } from './VipCardContext';
import ErrorReportingService from './ErrorReportingService';

// Create a Context for the cart
export const CartContext = createContext();

// Create a provider component
const CartProvider = ({ children }) => {
    const { selectedVipCard, selectedEmployee } = useContext(VipCardContext);
    const [cartItems, setCartItems] = useState([]);
    const [selectedVipCardDiscount, setSelectedVipCardDiscount] = useState(undefined);
    const [discountsForWholeCart, setDiscountsForWholeCart] = useState({ amount: 0, percentage: selectedVipCardDiscount || selectedVipCard?.discountAmount || selectedEmployee?.discount || 0 });
    const [serviceFeeAmount, setServiceFeeAmount] = useState(0);
    const [alreadySentCartItems, setAlreadySentCartItems] = useState([]);
    const [originalCartTotal, setOriginalCartTotal] = useState(cartItems.reduce((total, item) => total + (item.price * (item.quantity || 1)), 0));
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [serviceFeePercentage, setServiceFeePercentage] = useState( 0);

    // Calculate original cart total when cart items change
    useEffect(() => {
        const cartItemsTotal = cartItems.reduce((total, item) => total + (item.price * (item.quantity || 1)), 0);
        const sentItemsTotal = alreadySentCartItems?.length > 0 
            ? alreadySentCartItems.reduce((total, item) => 
                total + (item.remotePrice ? item.remotePrice * (item.amount || 1) : (item.price * (item.amount || 1))), 0) 
            : 0;

        setOriginalCartTotal(cartItemsTotal + sentItemsTotal);

        // Log cart update for debugging
        ErrorReportingService.info('Cart items updated', { 
            itemCount: cartItems.length,
            sentItemsCount: alreadySentCartItems.length
        }, 'CartContext');
    }, [cartItems, alreadySentCartItems]);

    useEffect(() => {
        if (selectedVipCard) {
            setSelectedVipCardDiscount(selectedVipCard.discountAmount)
            setDiscountsForWholeCart(prev => ({ ...prev, percentage: selectedVipCard.discountAmount }));
        }else if (selectedEmployee) {
            setSelectedVipCardDiscount(selectedEmployee.discount)
            setDiscountsForWholeCart(prev => ({ ...prev, percentage: selectedEmployee.discount }));
        }else{
            setDiscountsForWholeCart(prev => ({ ...prev, percentage: 0 }));
        }
    }, [selectedVipCard, selectedEmployee]);

    // Memoize expensive calculations to prevent unnecessary recalculations
    const alreadySentCartItemsPrice = useMemo(() => {
        return alreadySentCartItems?.reduce((total, item) => {
            const itemPrice = item.remotePrice ? item.remotePrice * (item.amount || 1) : (item.price * (item.amount || 1));
            const extrasPrice = item.extras ? item.extras.reduce((extraTotal, extra) => extraTotal + (extra.price || extra?.item?.sizes[0]?.price || 0), 0) * (item.amount || 1) : 0;
            return total + itemPrice + extrasPrice;
        }, 0);
    }, [alreadySentCartItems]);

    const productsPrice = useMemo(() => {
        const cartItemsTotal = cartItems.reduce((total, item) => {
            const itemPrice = item.remotePrice ? item.remotePrice : (item.price * (item.quantity || 1));
            const extrasPrice = item.extras ? item.extras.reduce((extraTotal, extra) => extraTotal + (extra.price || extra?.item?.sizes[0]?.price || 0), 0) : 0;
            return (total + itemPrice + (extrasPrice * (item?.quantity || 1)));
        }, 0);
        return cartItemsTotal + alreadySentCartItemsPrice;
    }, [cartItems, alreadySentCartItemsPrice]);

    // For now if there is item discount and whole cart discount, the whole cart discount will be calculated from, the already reduced item prices sum
    const totalDiscountPercentage = useMemo(() => {
        return Number(productsPrice) * (Number(discountsForWholeCart?.percentage)/100);
    }, [productsPrice, discountsForWholeCart?.percentage]);

    const cartTotal = useMemo(() => {
        return Number(productsPrice) - Number(discountsForWholeCart?.amount) - Number(totalDiscountPercentage);
    }, [productsPrice, discountsForWholeCart?.amount, totalDiscountPercentage]);

    const cartTotalWithServiceFee = useMemo(() => {
        return cartTotal + serviceFeeAmount;
    }, [cartTotal, serviceFeeAmount]);

    // Fetch service fee only once on mount and when productsPrice changes
    useEffect(() => {
        const fetchServiceFee = async () => {
            try {
                const restaurantPlace = await AsyncStorage.getItem('restaurantPlace');
                if (!restaurantPlace) return;

                const parsedPlace = JSON.parse(restaurantPlace);
                const serviceFee = parsedPlace.serviceFee;
                const showServiceFee = parsedPlace.showServiceFee;

                if (serviceFee && showServiceFee === true) {
                    const serviceFeeValue = Number(serviceFee);
                    setServiceFeePercentage(serviceFeeValue/100);

                    // Calculate service fee based on products price
                    const calculatedFee = Math.round(Number(((serviceFeeValue / 100) * Number(productsPrice)).toFixed(1)));
                    setServiceFeeAmount(calculatedFee);

                    ErrorReportingService.info('Service fee calculated', { 
                        serviceFeeValue,
                        productsPrice,
                        calculatedFee
                    }, 'CartContext');
                } else {
                    setServiceFeeAmount(0);
                }
            } catch (error) {
                ErrorReportingService.error('Error fetching service fee', { error }, 'CartContext');
                ErrorReportingService.reportError(error, { source: 'CartContext.fetchServiceFee' });
                setServiceFeeAmount(0);
            }
        };

        fetchServiceFee();
    }, [productsPrice]); // Only depend on productsPrice to avoid circular dependencies

    // Optimize functions with useCallback to prevent unnecessary recreations
    const addToCart = useCallback((item) => {
        ErrorReportingService.info('Adding item to cart', { itemId: item._id }, 'CartContext.addToCart');
        setCartItems(prevItems => [...prevItems, item]);
    }, []);

    const setCart = useCallback((newCartItems) => {
        ErrorReportingService.info('Setting cart items', { itemCount: newCartItems.length }, 'CartContext.setCart');
        setCartItems(newCartItems);
    }, []);

    const addComment = useCallback((itemId, comment) => {
        if (!comment || comment === "") return false;

        ErrorReportingService.info('Adding comment to item', { itemId, comment }, 'CartContext.addComment');

        setCartItems(prevItems => prevItems.map(item => {
            if (item._id === itemId) {
                const updatedItem = { ...item };
                if (!updatedItem.comments) {
                    updatedItem.comments = [comment];
                } else {
                    updatedItem.comments = [...updatedItem.comments, comment];
                }
                return updatedItem;
            }
            return item;
        }));

        setAlreadySentCartItems(prevItems => prevItems.map(item => {
            if (item._id === itemId) {
                const updatedItem = { ...item };
                if (!updatedItem.comments) {
                    updatedItem.comments = [comment];
                } else {
                    updatedItem.comments = [...updatedItem.comments, comment];
                }
                return updatedItem;
            }
            return item;
        }));

        return true;
    }, []);

    const addDiscount = useCallback((itemId, discount, openFrom) => {
        const discountAmount = Number(discount.amount);
        const discountPercentage = Number(discount.percentage);

        ErrorReportingService.info('Adding discount', { 
            itemId, 
            discountAmount, 
            discountPercentage, 
            openFrom 
        }, 'CartContext.addDiscount');

        if (openFrom === "cartItem") {
            setCartItems(prevItems => prevItems.map(item => {
                if (item._id === itemId) {
                    const updatedItem = { ...item };
                    updatedItem.discounts = { amount: discountAmount, percentage: discountPercentage };
                    const fullPrice = updatedItem.price * updatedItem.quantity;
                    const discountFromPercentage = fullPrice * (updatedItem.discounts.percentage / 100);
                    updatedItem.remotePrice = fullPrice - updatedItem.discounts.amount - discountFromPercentage;
                    return updatedItem;
                }
                return item;
            }));
        } else if (openFrom === "cartTotal") {
            setDiscountsForWholeCart({ amount: discountAmount, percentage: discountPercentage });
        }
    }, []);

    const removeFromCart = useCallback((itemId) => {
        ErrorReportingService.info('Removing item from cart', { itemId }, 'CartContext.removeFromCart');

        setCartItems(prevItems => prevItems.filter(item => item._id !== itemId));
        setAlreadySentCartItems(prevItems => prevItems.filter(item => item._id !== itemId));
    }, []);

    const deleteCart = useCallback(() => {
        ErrorReportingService.info('Deleting cart', {}, 'CartContext.deleteCart');

        setCartItems([]);

        if(selectedVipCard){
            setDiscountsForWholeCart({ 
                amount: 0, 
                percentage: selectedVipCardDiscount || selectedVipCard?.discountAmount || selectedVipCard?.discount || 0
            });
        } else {
            setDiscountsForWholeCart({ amount: 0, percentage: 0 });
        }
    }, [selectedVipCard, selectedVipCardDiscount]);

    // Define callbacks at the top level to avoid hooks inside hooks
    const setDiscountsForWholeCartCallback = useCallback((value) => {
        ErrorReportingService.info('Setting discounts for whole cart', { value }, 'CartContext');
        setDiscountsForWholeCart(value);
    }, []);

    const setCartItemsCallback = useCallback((value) => {
        ErrorReportingService.info('Setting cart items directly', { itemCount: value.length }, 'CartContext');
        setCartItems(value);
    }, []);

    const setAlreadySentCartItemsCallback = useCallback((value) => {
        ErrorReportingService.info('Setting already sent cart items', { itemCount: value.length }, 'CartContext');
        setAlreadySentCartItems(value);
    }, []);

    const setPaymentModalVisibleCallback = useCallback((value) => {
        ErrorReportingService.info('Setting payment modal visibility', { visible: value }, 'CartContext');
        setPaymentModalVisible(value);
    }, []);

    // Memoize the context value to prevent unnecessary re-renders of consumers
    const contextValue = useMemo(() => ({
        cartItems, 
        addToCart, 
        removeFromCart, 
        setCart, 
        cartTotal, 
        deleteCart, 
        addComment, 
        addDiscount, 
        originalCartTotal, 
        productsPrice, 
        discountsForWholeCart, 
        setDiscountsForWholeCart: setDiscountsForWholeCartCallback,
        setCartItems: setCartItemsCallback,
        serviceFeeAmount, 
        cartTotalWithServiceFee, 
        alreadySentCartItems, 
        setAlreadySentCartItems: setAlreadySentCartItemsCallback,
        paymentModalVisible, 
        setPaymentModalVisible: setPaymentModalVisibleCallback,
        serviceFeePercentage
    }), [
        cartItems, 
        addToCart, 
        removeFromCart, 
        setCart, 
        cartTotal, 
        deleteCart, 
        addComment, 
        addDiscount, 
        originalCartTotal, 
        productsPrice, 
        discountsForWholeCart, 
        setDiscountsForWholeCartCallback,
        setCartItemsCallback,
        serviceFeeAmount, 
        cartTotalWithServiceFee, 
        alreadySentCartItems, 
        setAlreadySentCartItemsCallback,
        paymentModalVisible, 
        setPaymentModalVisibleCallback,
        serviceFeePercentage
    ]);

    return (
        <CartContext.Provider value={contextValue}>
            {children}
        </CartContext.Provider>
    );
};
export default React.memo(CartProvider);
